import React, { useState, useRef } from 'react';
import { Upload, X, Loader, Crop } from 'lucide-react';
// MIGRATION FIREBASE COMPLETE: Plus de backend Python
import { uploadService } from '../services/firebaseService';
import ImageCropModal from './ImageCropModal';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../firebase/config';

/**
 * Version compacte d'ImageUpload pour les listes (ex: choix dans une option)
 * - Affiche une miniature 48x48
 * - Ouvre le modal de recadrage à l'upload
 * - Bouton supprimer intégré
 */
export const ImageUploadMini = ({
  currentImage,
  onImageChange,
  folder = "options",
  aspectRatio = 1,
  size = 48,
  disabled = false,
  placeholder = "Image"
}) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentImage || null);
  const fileInputRef = useRef(null);

  // États pour le crop
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [loadingCrop, setLoadingCrop] = useState(false);

  // Convertir une URL en blob local via Cloud Function (bypass CORS)
  const fetchImageAsBlob = async (imageUrl) => {
    try {
      // CLOUD FUNCTION: proxyImage retourne l'image en base64
      const functions = getFunctions(app, 'europe-west1');
      const proxyImage = httpsCallable(functions, 'proxyImage');
      const result = await proxyImage({ imageUrl });

      // Convertir dataUrl en blob URL
      const response = await fetch(result.data.dataUrl);
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Erreur fetch image via Cloud Function:', error);
      return null;
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Vérifier que c'est une image
    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner une image');
      return;
    }

    // Ouvrir le modal de recadrage
    const reader = new FileReader();
    reader.onloadend = () => {
      setImageToCrop(reader.result);
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);
  };

  // Upload d'une image (blob)
  const uploadImage = async (blob) => {
    setUploading(true);
    try {
      // FIREBASE DIRECT
      const result = await uploadService.uploadImage(blob, folder);
      const imageUrl = result.url;
      onImageChange(imageUrl);
      setPreview(imageUrl);
    } catch (error) {
      console.error('Erreur upload:', error);
      alert('Erreur lors de l\'upload de l\'image');
    } finally {
      setUploading(false);
    }
  };

  // Callback après crop
  const handleCropComplete = async (croppedBlob) => {
    if (imageToCrop && imageToCrop.startsWith('blob:')) {
      URL.revokeObjectURL(imageToCrop);
    }
    setShowCropModal(false);
    setImageToCrop(null);
    await uploadImage(croppedBlob);
  };

  // Ouvrir le crop pour une image existante
  const handleCropExisting = async (e) => {
    e.stopPropagation();
    if (!preview) return;

    if (preview.startsWith('data:')) {
      setImageToCrop(preview);
      setShowCropModal(true);
      return;
    }

    setLoadingCrop(true);
    try {
      const blobUrl = await fetchImageAsBlob(preview);
      if (blobUrl) {
        setImageToCrop(blobUrl);
        setShowCropModal(true);
      } else {
        alert('Impossible de charger l\'image pour le recadrage');
      }
    } catch (error) {
      console.error('Erreur chargement image:', error);
    } finally {
      setLoadingCrop(false);
    }
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    setPreview(null);
    onImageChange('');
  };

  const triggerFileInput = () => {
    if (!disabled && !uploading) {
      fileInputRef.current?.click();
    }
  };

  // Sync preview with currentImage prop
  React.useEffect(() => {
    setPreview(currentImage || null);
  }, [currentImage]);

  return (
    <>
      {/* Input file caché */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
        disabled={disabled || uploading}
      />

      {preview ? (
        // Affichage de la miniature avec actions
        <div className="relative flex-shrink-0 group" style={{ width: size, height: size }}>
          <img
            src={preview}
            alt="Preview"
            className="w-full h-full object-cover rounded-lg border-2 border-gray-200 cursor-pointer"
            onClick={triggerFileInput}
          />

          {/* Overlay de chargement */}
          {(uploading || loadingCrop) && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
              <Loader className="w-5 h-5 text-white animate-spin" />
            </div>
          )}

          {/* Boutons d'action (visibles au hover) */}
          <div className="absolute -top-1 -right-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
            {/* Bouton Recadrer */}
            <button
              type="button"
              onClick={handleCropExisting}
              disabled={uploading || loadingCrop}
              className="p-1 bg-orange-500 text-white rounded-full hover:bg-orange-600 shadow text-xs"
              title="Recadrer"
            >
              <Crop className="w-3 h-3" />
            </button>

            {/* Bouton Supprimer */}
            <button
              type="button"
              onClick={handleRemove}
              disabled={uploading}
              className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600 shadow text-xs"
              title="Supprimer"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      ) : (
        // Zone d'upload vide
        <div
          onClick={triggerFileInput}
          className={`flex-shrink-0 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary hover:bg-gray-50 transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          style={{ width: size, height: size }}
        >
          {uploading ? (
            <Loader className="w-5 h-5 text-gray-400 animate-spin" />
          ) : (
            <Upload className="w-5 h-5 text-gray-400" />
          )}
        </div>
      )}

      {/* Modal de recadrage */}
      <ImageCropModal
        isOpen={showCropModal}
        onClose={() => {
          if (imageToCrop && imageToCrop.startsWith('blob:')) {
            URL.revokeObjectURL(imageToCrop);
          }
          setShowCropModal(false);
          setImageToCrop(null);
        }}
        imageSrc={imageToCrop}
        onCropComplete={handleCropComplete}
        aspectRatio={aspectRatio}
        title="Recadrer l'image"
      />
    </>
  );
};

export default ImageUploadMini;
